import { useMemo } from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { palette } from '../../styles/tokens';
import type { ScheduleEntry } from '../../types';
import { extractDate, extractHour, formatTime12, getUserName, getUserInitials, getEntryColor, instantToBusinessParts, ENTRY_TYPE_LABELS, type ScheduleUser } from './scheduleHelpers';

interface DayViewProps {
  date: Date;
  entries: ScheduleEntry[];
  users: ScheduleUser[];
  onEntryClick: (entry: ScheduleEntry) => void;
}

const START_HOUR = 6;
const END_HOUR = 20;
const HOURS = Array.from({ length: END_HOUR - START_HOUR }, (_, i) => START_HOUR + i);
const COL_WIDTH = 140;
const ROW_HEIGHT = 48;

function formatHour(h: number): string { const ampm = h >= 12 ? 'PM' : 'AM'; return `${h % 12 || 12} ${ampm}`; }

function DraggableBlock({ entry, colIdx, onEntryClick }: { entry: ScheduleEntry; colIdx: number; onEntryClick: (e: ScheduleEntry) => void }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: entry.id, data: { entry } });
  const startH = extractHour(entry.start_at);
  const endH = extractHour(entry.end_at);
  const top = 36 + (startH - START_HOUR) * ROW_HEIGHT;
  const height = Math.max((endH - startH) * ROW_HEIGHT, 20);
  const color = getEntryColor(entry);
  const label = ENTRY_TYPE_LABELS[entry.type as keyof typeof ENTRY_TYPE_LABELS] ?? '';

  return (
    <div ref={setNodeRef} {...listeners} {...attributes}
      onClick={() => { if (!isDragging) onEntryClick(entry); }}
      style={{
        position: 'absolute', top, left: colIdx * COL_WIDTH + 4, width: COL_WIDTH - 8, height: height - 2,
        background: color + '18', border: `1px solid ${color}50`, borderLeft: `3px solid ${color}`,
        borderRadius: 4, padding: '3px 6px', cursor: isDragging ? 'grabbing' : 'grab', overflow: 'hidden',
        display: 'flex', flexDirection: 'column', fontFamily: 'inherit', textAlign: 'left',
        opacity: isDragging ? 0.3 : 1, zIndex: isDragging ? 10 : 1, touchAction: 'none',
      }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: palette.g800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{entry.title}</div>
      <div style={{ fontSize: 10, color: palette.g500, marginTop: 1 }}>{formatTime12(entry.start_at)} – {formatTime12(entry.end_at)}</div>
      {height > 40 && <div style={{ fontSize: 9, color, fontWeight: 500, marginTop: 2 }}>{label}</div>}
    </div>
  );
}

function DroppableColumn({ userId, index, children }: { userId: string; index: number; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id: `day-col-${userId}` });
  return (
    <div ref={setNodeRef} style={{
      position: 'absolute', left: index * COL_WIDTH, top: 36, width: COL_WIDTH,
      bottom: 0, background: isOver ? palette.accentWash + '60' : 'transparent',
      transition: 'background 0.15s', zIndex: 0,
    }}>
      {children}
    </div>
  );
}

export function DayView({ date, entries, users, onEntryClick }: DayViewProps) {
  const dateStr = useMemo(() => {
    const y = date.getFullYear(); const m = String(date.getMonth() + 1).padStart(2, '0'); const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }, [date]);

  const dayEntries = useMemo(() => entries.filter(e => extractDate(e.start_at) === dateStr && e.status !== 'cancelled'), [entries, dateStr]);
  const allDayEntries = useMemo(() => dayEntries.filter(e => e.all_day), [dayEntries]);
  const timedEntries = useMemo(() => dayEntries.filter(e => !e.all_day), [dayEntries]);

  const staffIds = useMemo(() => {
    const ids = new Set<string>();
    for (const e of timedEntries) { const uid = e.assigned_to?.[0]; if (uid) ids.add(uid); }
    if (timedEntries.some(e => !e.assigned_to?.length)) ids.add('__unassigned');
    if (ids.size === 0) for (const u of users) ids.add(u.id);
    return Array.from(ids);
  }, [timedEntries, users]);

  // A2 P4 — "today" + the now-line are business-tz, so an operator on a device in
  // another tz still sees the line correctly placed (and only on the real today).
  const nowBiz = instantToBusinessParts(new Date().toISOString());
  const isToday = dateStr === `${nowBiz.year}-${String(nowBiz.month).padStart(2, '0')}-${String(nowBiz.day).padStart(2, '0')}`;
  const nowOffset = isToday ? (nowBiz.hour + nowBiz.minute / 60 - START_HOUR) * ROW_HEIGHT : -1;

  return (
    <div style={{ overflowX: 'auto' }}>
      {allDayEntries.length > 0 && (
        <div style={{ display: 'flex', gap: 6, padding: '8px 12px', borderBottom: `1px solid ${palette.g150}`, background: palette.g50, flexWrap: 'wrap' }}>
          {allDayEntries.map(e => (
            <button key={e.id} onClick={() => onEntryClick(e)} style={{ padding: '4px 10px', borderRadius: 4, border: 'none', cursor: 'pointer', fontFamily: 'inherit', background: getEntryColor(e) + '20', color: getEntryColor(e), fontSize: 12, fontWeight: 500 }}>{e.title}</button>
          ))}
        </div>
      )}
      <div style={{ display: 'flex' }}>
        <div style={{ width: 60, flexShrink: 0 }}>
          <div style={{ height: 36, borderBottom: `1px solid ${palette.g150}` }} />
          {HOURS.map(h => (
            <div key={h} style={{ height: ROW_HEIGHT, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', paddingRight: 8, paddingTop: 2, fontSize: 10, color: palette.g400, fontWeight: 500, borderBottom: `1px solid ${palette.g100}` }}>{formatHour(h)}</div>
          ))}
        </div>
        <div style={{ position: 'relative', minWidth: staffIds.length * COL_WIDTH }}>
          <div style={{ display: 'flex', height: 36, borderBottom: `1px solid ${palette.g150}` }}>
            {staffIds.map(uid => {
              const name = uid === '__unassigned' ? 'Unassigned' : getUserName(uid, users);
              const initials = uid === '__unassigned' ? '?' : getUserInitials(uid, users);
              return (
                <div key={uid} style={{ width: COL_WIDTH, display: 'flex', alignItems: 'center', gap: 6, padding: '0 8px', borderRight: `1px solid ${palette.g100}`, fontSize: 12, fontWeight: 600, color: palette.g700 }}>
                  <div style={{ width: 22, height: 22, borderRadius: 11, background: palette.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 600, color: palette.white }}>{initials}</div>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
                </div>
              );
            })}
          </div>
          {HOURS.map(h => (
            <div key={h} style={{ display: 'flex', height: ROW_HEIGHT, borderBottom: `1px solid ${palette.g100}` }}>
              {staffIds.map(uid => <div key={uid} style={{ width: COL_WIDTH, borderRight: `1px solid ${palette.g100}` }} />)}
            </div>
          ))}
          {/* Droppable columns */}
          {staffIds.map((uid, idx) => (
            <DroppableColumn key={uid} userId={uid} index={idx}>{null}</DroppableColumn>
          ))}
          {/* Draggable entries */}
          {timedEntries.map(entry => {
            const uid = entry.assigned_to?.[0] ?? '__unassigned';
            const colIdx = staffIds.indexOf(uid);
            if (colIdx === -1) return null;
            return <DraggableBlock key={entry.id} entry={entry} colIdx={colIdx} onEntryClick={onEntryClick} />;
          })}
          {/* NOW indicator */}
          {isToday && nowOffset >= 0 && nowOffset <= HOURS.length * ROW_HEIGHT && (
            <div style={{ position: 'absolute', top: 36 + nowOffset, left: 0, right: 0, height: 2, background: palette.red, zIndex: 10, pointerEvents: 'none' }}>
              <div style={{ position: 'absolute', left: -4, top: -3, width: 8, height: 8, borderRadius: 4, background: palette.red }} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
